<template>
  <v-card outlined class="mb-4">
    <v-card-text>
      <v-alert type="success" outlined dense class="mb-4">
        <v-icon left>mdi-check-circle</v-icon>
        Normativ je automatski učitan. Provjerite količine i odaberite skladišta.
      </v-alert>

      <v-row>
        <v-col cols="12">
          <div class="d-flex align-center mb-3">
            <h3 class="text-h6">Sastojci iz normativa</h3>
            <v-spacer></v-spacer>
            <v-chip color="primary" outlined>
              <v-icon left small>mdi-calculator</v-icon>
              {{ ingredients.length }} sastojaka
            </v-chip>
          </div>

          <!-- Normative ingredients -->
          <v-card
            v-for="(ingredient, index) in ingredients"
            :key="`norm-${ingredient.id || index}`"
            outlined
            :class="ingredient.fromNormative ? 'mb-3 blue lighten-5' : 'mb-3'"
          >
            <v-card-subtitle v-if="ingredient.fromNormative" class="pb-0">
              <v-chip x-small color="primary">Iz normativa</v-chip>
            </v-card-subtitle>
            <v-card-text>
              <v-row align="center">
                <v-col cols="12" md="4">
                  <div class="d-flex align-center">
                    <v-icon left color="primary">mdi-package-variant</v-icon>
                    <div>
                      <div class="font-weight-bold">{{ ingredient.name }}</div>
                      <div class="caption grey--text">
                        Po jedinici: {{ ingredient.quantityPerUnit.toFixed(3) }} {{ ingredient.unit }}
                      </div>
                    </div>
                  </div>
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    v-model.number="ingredient.quantity"
                    type="number"
                    label="Količina"
                    outlined
                    dense
                    :suffix="ingredient.unit"
                    :rules="[rules.required, rules.positive, rules.maxStock(ingredient)]"
                    :hint="`Dostupno: ${ingredient.availableStock} ${ingredient.unit}`"
                    persistent-hint
                    @input="onQuantityChange(ingredient)"
                  >
                    <template v-slot:prepend-inner>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-icon
                            v-on="on"
                            small
                            color="info"
                            @click="resetToNormative(ingredient)"
                          >
                            mdi-refresh
                          </v-icon>
                        </template>
                        <span>Vrati na normativ</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </v-col>

                <v-col cols="12" md="5">
                  <v-row no-gutters>
                    <v-col cols="12">
                      <v-select
                        v-model="ingredient.warehouse"
                        :items="warehouses"
                        item-text="name"
                        item-value="id"
                        return-object
                        label="Skladište"
                        prepend-icon="mdi-warehouse"
                        outlined
                        dense
                        :rules="[rules.required]"
                      >
                        <template v-slot:item="{ item }">
                          <v-list-item-content>
                            <v-list-item-title>{{ item.name }}</v-list-item-title>
                            <v-list-item-subtitle>{{ item.location }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </template>
                      </v-select>
                    </v-col>
                    <v-col cols="12">
                      <div class="px-2">
                        <div class="caption grey--text mb-1">Kalo (%)</div>
                        <v-slider
                          :value="getWastageValue(index)"
                          @input="onWastageSliderChange(index, $event)"
                          min="0"
                          max="20"
                          step="0.1"
                          thumb-label="always"
                          color="orange darken-1"
                          track-color="grey lighten-2"
                          thumb-color="orange"
                          dense
                          hide-details
                          :key="`wastage-${index}`"
                        >
                          <template v-slot:prepend>
                            <span class="caption grey--text font-weight-bold">0%</span>
                          </template>
                          <template v-slot:append>
                            <span class="caption grey--text font-weight-bold">20%</span>
                          </template>
                          <template v-slot:thumb-label="{ value }">
                            {{ value.toFixed(1) }}%
                          </template>
                        </v-slider>
                        <div v-if="getWastageValue(index) > 0" class="text-center mt-1">
                          <v-chip x-small color="orange lighten-4" class="orange--text text--darken-2">
                            <v-icon x-small left color="orange darken-2">mdi-arrow-down</v-icon>
                            <strong>{{ getWastageAmount(ingredient, index).toFixed(3) }} {{ ingredient.unit }}</strong>
                          </v-chip>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-col>

                <v-col cols="12" md="1" v-if="!ingredient.fromNormative">
                  <v-btn
                    icon
                    color="error"
                    @click="removeIngredient(index)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>

              <v-row v-if="ingredient.quantity !== ingredient.originalQuantity" class="mt-0">
                <v-col cols="12">
                  <v-alert type="warning" dense text>
                    <v-icon left small>mdi-alert</v-icon>
                    Promijenjena količina od normativa!
                    Normativ: {{ ingredient.originalQuantity.toFixed(2) }} {{ ingredient.unit }} →
                    Trenutno: {{ ingredient.quantity.toFixed(2) }} {{ ingredient.unit }}
                    ({{ ((ingredient.quantity / ingredient.originalQuantity - 1) * 100).toFixed(1) }}%)
                  </v-alert>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-divider class="my-4"></v-divider>

          <!-- Add additional ingredients -->
          <div class="d-flex align-center mb-3">
            <h3 class="text-h6">Dodatni sastojci</h3>
            <v-spacer></v-spacer>
            <v-btn
              color="success"
              outlined
              small
              @click="addAdditionalIngredient"
            >
              <v-icon left small>mdi-plus</v-icon>
              Dodaj sastojak izvan normativa
            </v-btn>
          </div>

          <v-alert v-if="additionalIngredients.length === 0" type="info" dense outlined>
            <v-icon left small>mdi-information</v-icon>
            Nema dodatnih sastojaka. Možete dodati sastojke koji nisu u normativu.
          </v-alert>

          <!-- Additional ingredients cards -->
          <v-card
            v-for="(additional, index) in additionalIngredients"
            :key="`add-${index}`"
            outlined
            class="mb-3 orange lighten-5"
          >
            <v-card-subtitle class="pb-0">
              <v-chip x-small color="orange">Dodatno</v-chip>
            </v-card-subtitle>
            <v-card-text>
              <v-row align="center">
                <v-col cols="12" md="4">
                  <v-autocomplete
                    v-model="additional.item"
                    :items="availableItems"
                    item-text="name"
                    item-value="id"
                    return-object
                    label="Artikl"
                    prepend-icon="mdi-package-variant"
                    outlined
                    dense
                    clearable
                    @change="onAdditionalItemChange(additional)"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.name }}</v-list-item-title>
                        <v-list-item-subtitle>
                          Šifra: {{ item.code }} | Stanje: {{ item.stock }} {{ item.unit }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    v-model.number="additional.quantity"
                    type="number"
                    label="Količina"
                    outlined
                    dense
                    :suffix="additional.unit"
                    :rules="[rules.required, rules.positive]"
                    :disabled="!additional.item"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="4">
                  <v-select
                    v-model="additional.warehouse"
                    :items="warehouses"
                    item-text="name"
                    item-value="id"
                    return-object
                    label="Skladište"
                    prepend-icon="mdi-warehouse"
                    outlined
                    dense
                    :disabled="!additional.item"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.name }}</v-list-item-title>
                        <v-list-item-subtitle>{{ item.location }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-select>
                </v-col>

                <v-col cols="12" md="1">
                  <v-btn
                    icon
                    color="error"
                    @click="removeAdditionalIngredient(index)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Summary -->
          <v-card outlined color="grey lighten-4" class="mt-4">
            <v-card-text>
              <v-row>
                <v-col cols="12" md="6">
                  <h4>Sažetak sastojaka</h4>
                  <div class="mt-2">
                    <div>Iz normativa: <strong>{{ ingredients.length }}</strong></div>
                    <div>Dodatno: <strong>{{ additionalIngredients.length }}</strong></div>
                    <div>Ukupno: <strong>{{ allIngredients.length }}</strong></div>
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="text-right">
                  <h4>Ukupna težina sirovine</h4>
                  <div class="text-h5 primary--text mt-2">
                    {{ totalWeight.toFixed(2) }} kg
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'IngredientSelector',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    product: {
      type: Object,
      default: () => null
    },
    desiredQuantity: {
      type: Number,
      default: 0
    },
    warehouses: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      ingredients: [],
      additionalIngredients: [],
      materialWastage: {}, // { ingredientId: wastagePercentage (0-20 with 0.1 precision) }

      // Available items for additional ingredients
      availableItems: [
        { id: 1, name: 'Junetina sirovina', code: 'JUN-001', stock: 150, unit: 'kg', pricePerKg: 12.50 },
        { id: 2, name: 'Soja sirovina', code: 'SOJ-001', stock: 200, unit: 'kg', pricePerKg: 3.20 },
        { id: 3, name: 'Svinjetina sirovina', code: 'SVI-001', stock: 100, unit: 'kg', pricePerKg: 8.90 },
        { id: 4, name: 'Mirodije mix', code: 'MIR-001', stock: 50, unit: 'kg', pricePerKg: 15.00 },
        { id: 5, name: 'Sol', code: 'SOL-001', stock: 80, unit: 'kg', pricePerKg: 0.50 },
        { id: 6, name: 'Papar crni', code: 'PAP-001', stock: 20, unit: 'kg', pricePerKg: 25.00 },
        { id: 7, name: 'Luk suh', code: 'LUK-001', stock: 60, unit: 'kg', pricePerKg: 1.80 },
        { id: 8, name: 'Češnjak', code: 'CES-001', stock: 30, unit: 'kg', pricePerKg: 8.50 }
      ],

      rules: {
        required: v => !!v || 'Obavezno polje',
        positive: v => v > 0 || 'Količina mora biti veća od 0',
        maxStock: (ingredient) => (v) => {
          if (!ingredient.availableStock) return true
          return v <= ingredient.availableStock || `Maksimalno dostupno: ${ingredient.availableStock} kg`
        }
      }
    }
  },
  computed: {
    allIngredients () {
      return [...this.ingredients, ...this.additionalIngredients.filter(a => a.item)]
    },
    totalWeight () {
      // Calculate total weight after wastage (effective weight)
      return this.allIngredients.reduce((sum, ing, index) => {
        const quantity = ing.quantity || 0
        const wastageAmount = this.getWastageAmount(ing, index)
        const effectiveQuantity = quantity - wastageAmount
        return sum + effectiveQuantity
      }, 0)
    }
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler (newVal) {
        if (newVal && newVal.length > 0) {
          this.ingredients = JSON.parse(JSON.stringify(newVal)).map(ing => ({
            ...ing,
            originalQuantity: ing.quantity
          }))

          // Initialize wastage to 0% for all ingredients using index
          newVal.forEach((ingredient, index) => {
            if (this.materialWastage[index] === undefined) {
              this.$set(this.materialWastage, index, 0)
            }
          })
        }
      }
    }
  },
  methods: {
    onQuantityChange (ingredient) {
      this.emitChanges()
    },
    resetToNormative (ingredient) {
      ingredient.quantity = ingredient.quantityPerUnit * this.desiredQuantity
      this.emitChanges()
    },
    onWastageSliderChange (index, percentage) {
      // Update the wastage percentage from slider (0-20 with 0.1 precision)
      const clampedPercentage = Math.max(0, Math.min(20, Number(percentage) || 0))
      this.$set(this.materialWastage, index, clampedPercentage)
      this.emitChanges()
    },
    getWastageValue (index) {
      // Get wastage value for specific ingredient by index
      return this.materialWastage[index] || 0
    },
    getWastageAmount (ingredient, index) {
      // Calculate wastage amount based on percentage
      const wastagePercentage = this.materialWastage[index] || 0
      return (ingredient.quantity * wastagePercentage) / 100
    },
    removeIngredient (index) {
      this.ingredients.splice(index, 1)
      this.emitChanges()
    },
    addAdditionalIngredient () {
      this.additionalIngredients.push({
        item: null,
        quantity: 0,
        unit: 'kg',
        warehouse: null,
        fromNormative: false
      })
    },
    removeAdditionalIngredient (index) {
      this.additionalIngredients.splice(index, 1)
      this.emitChanges()
    },
    onAdditionalItemChange (additional) {
      if (additional.item) {
        additional.name = additional.item.name
        additional.unit = additional.item.unit
        additional.availableStock = additional.item.stock
        additional.pricePerKg = additional.item.pricePerKg
      }
      this.emitChanges()
    },
    emitChanges () {
      // Emit ingredients with wastage information
      const ingredientsWithWastage = this.allIngredients.map((ing, index) => ({
        ...ing,
        wastagePercentage: this.materialWastage[index] || 0,
        wastageAmount: this.getWastageAmount(ing, index),
        effectiveQuantity: ing.quantity - this.getWastageAmount(ing, index)
      }))
      this.$emit('input', ingredientsWithWastage)
    }
  }
}
</script>

<style scoped>
.v-card {
  transition: all 0.3s;
}
</style>
